if (!customElements.get('apella-slideshow')) {
  class ApellaSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.stage = this.querySelector('[data-slideshow-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.stage || this.slides.length === 0) return;

      this.initialized = true;
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.indicators = Array.from(this.querySelectorAll('[data-slide-to]'));
      this.previousButton = this.querySelector('[data-previous]');
      this.nextButton = this.querySelector('[data-next]');
      this.status = this.querySelector('[data-slide-status]');
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.autoplayDelay = Math.max(3000, Number.parseInt(this.dataset.speed, 10) || 5000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.reducedMotion.matches;
      this.focusPaused = false;
      this.isAnimating = false;
      this.transitionSource = null;
      this.contentRevealToken = 0;
      this.pointerStart = null;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.bindEvents();
      this.resizeObserver = new ResizeObserver(() => this.syncControlPosition());
      const firstMedia = this.slides[0]?.querySelector('.apella-slideshow__media');
      if (firstMedia) this.resizeObserver.observe(firstMedia);
      this.syncControlPosition();
      this.syncSlides();
      this.syncControls();
      this.scheduleAutoplay();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.clearAutoplay();
      this.clearTransition();
      this.clearNavigationPush();
      this.initialized = false;
    }

    bindEvents() {
      this.previousButton?.addEventListener(
        'click',
        () => this.navigateFromArrow(this.previousButton, 'previous'),
        { signal: this.signal }
      );
      this.nextButton?.addEventListener(
        'click',
        () => this.navigateFromArrow(this.nextButton, 'next'),
        { signal: this.signal }
      );

      this.indicators.forEach((indicator) => {
        indicator.addEventListener(
          'click',
          () => {
            const targetIndex = Number.parseInt(indicator.dataset.slideTo, 10);
            const direction = targetIndex >= this.currentIndex ? 'next' : 'previous';
            this.focusPaused = false;
            this.goTo(targetIndex, direction, 'indicator');
          },
          { signal: this.signal }
        );
      });

      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal: this.signal });
      this.stage.addEventListener('pointercancel', () => this.cancelPointer(), { signal: this.signal });
      this.stage.addEventListener('click', (event) => {
        if (event.detail !== 0 && performance.now() < this.suppressImageClickUntil && event.target.closest('.apella-slideshow__image-link')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal: this.signal });

      this.addEventListener(
        'focusin',
        () => {
          this.focusPaused = true;
          this.clearAutoplay();
        },
        { signal: this.signal }
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.focusPaused = false;
          this.scheduleAutoplay();
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) this.clearAutoplay();
          else this.scheduleAutoplay();
        },
        { signal: this.signal }
      );
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal: this.signal });
      this.reducedMotion.addEventListener(
        'change',
        (event) => {
          this.userPaused = event.matches;
          if (event.matches) this.clearAutoplay();
          else this.scheduleAutoplay();
          this.syncControls();
        },
        { signal: this.signal }
      );
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPrevious('keyboard');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNext('keyboard');
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, 'previous', 'keyboard');
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1, 'next', 'keyboard');
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('a:not(.apella-slideshow__image-link), button, input, select, textarea')) return;
      this.suppressImageClickUntil = 0;

      this.pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
      };
      const captureTarget = event.target.closest('.apella-slideshow__image-link') || this.stage;
      captureTarget.setPointerCapture?.(event.pointerId);
    }

    onPointerUp(event) {
      if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      const elapsed = performance.now() - this.pointerStart.time;
      this.cancelPointer();

      if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
        this.suppressImageClickUntil = performance.now() + 500;
      }

      if (elapsed > 700 || Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX < 0) this.showNext('swipe');
      else this.showPrevious('swipe');
    }

    cancelPointer() {
      this.pointerStart = null;
    }

    showNext(source) {
      this.goTo(this.currentIndex + 1, 'next', source);
    }

    showPrevious(source) {
      this.goTo(this.currentIndex - 1, 'previous', source);
    }

    navigateFromArrow(button, direction) {
      if (!button) return;

      // Clicking a navigation arrow leaves it focused; that focus must not
      // convert a manual slide change into a persistent autoplay pause.
      this.focusPaused = false;
      this.animateNavigationPush(button);
      if (direction === 'next') this.showNext('control');
      else this.showPrevious('control');
    }

    animateNavigationPush(button) {
      this.clearNavigationPush();
      this.activePushButton = button;
      button.classList.remove('is-pushing');
      void button.getBoundingClientRect();
      button.classList.add('is-pushing');
      this.navigationPushTimer = window.setTimeout(() => this.clearNavigationPush(), 640);
    }

    clearNavigationPush() {
      if (this.navigationPushTimer) window.clearTimeout(this.navigationPushTimer);
      this.navigationPushTimer = null;
      this.activePushButton?.classList.remove('is-pushing');
      this.activePushButton = null;
    }

    goTo(targetIndex, direction = 'next', source = 'control') {
      if (this.slides.length < 2) return;

      const normalizedIndex = (targetIndex + this.slides.length) % this.slides.length;
      if (normalizedIndex === this.currentIndex) return;

      if (this.isAnimating) this.completeTransition();
      this.clearAutoplay();
      this.clearContentReveal();

      const outgoingSlide = this.slides[this.currentIndex];
      const incomingSlide = this.slides[normalizedIndex];

      this.isAnimating = true;
      this.transitionSource = source;
      outgoingSlide.classList.remove('is-active');
      outgoingSlide.classList.add('is-exiting');
      outgoingSlide.dataset.direction = direction;

      incomingSlide.classList.remove('is-exiting');
      incomingSlide.classList.remove('is-content-revealing');
      incomingSlide.classList.add('is-entering');
      incomingSlide.dataset.direction = direction;
      incomingSlide.setAttribute('aria-hidden', 'false');
      this.setSlideInteractive(outgoingSlide, false);
      this.setSlideInteractive(incomingSlide, true);

      this.currentIndex = normalizedIndex;
      this.syncControls();
      if (source !== 'autoplay' && this.status) {
        this.status.textContent = `Slide ${this.currentIndex + 1} of ${this.slides.length}`;
      }

      this.scheduleContentReveal(incomingSlide);
    }

    completeTransition() {
      const completedSource = this.transitionSource;
      this.clearTransition();
      this.isAnimating = false;
      this.transitionSource = null;

      this.slides.forEach((slide, index) => {
        const isCurrent = index === this.currentIndex;
        slide.classList.toggle('is-active', isCurrent);
        slide.classList.remove('is-entering', 'is-exiting', 'is-content-revealing', 'is-initializing');
        slide.removeAttribute('data-direction');
        slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
        this.setSlideInteractive(slide, isCurrent);
      });

      const usesExtendedCycle = completedSource === 'autoplay' || completedSource === 'indicator';
      this.scheduleAutoplay(usesExtendedCycle ? 2000 : 0);
    }

    scheduleContentReveal(slide) {
      const revealToken = ++this.contentRevealToken;
      const imageSettleDelay = this.reducedMotion.matches ? 0 : 780;
      const contentDuration = this.reducedMotion.matches ? 220 : 840;

      const settleDelay = new Promise((resolve) => {
        this.contentDelayTimer = window.setTimeout(resolve, imageSettleDelay);
      });

      Promise.all([settleDelay, this.waitForSlideImage(slide)]).then(() => {
        if (revealToken !== this.contentRevealToken || !this.isAnimating || this.slides[this.currentIndex] !== slide) {
          return;
        }

        slide.classList.add('is-content-revealing');
        const hasContent = Boolean(slide.querySelector('[data-succession-item]'));
        this.transitionTimer = window.setTimeout(
          () => this.completeTransition(),
          hasContent ? contentDuration : 40
        );
      });
    }

    waitForSlideImage(slide) {
      const image = slide.querySelector('img');
      if (!image || (image.complete && image.naturalWidth > 0)) return Promise.resolve();

      return new Promise((resolve) => {
        let timeout;
        const finish = () => {
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          window.clearTimeout(timeout);
          resolve();
        };

        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        timeout = window.setTimeout(finish, 2500);
      });
    }

    syncSlides() {
      this.slides.forEach((slide, index) => {
        const isCurrent = index === this.currentIndex;
        slide.classList.toggle('is-active', isCurrent);
        slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
        this.setSlideInteractive(slide, isCurrent);
      });
    }

    setSlideInteractive(slide, interactive) {
      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (interactive) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', '-1');
      });
    }

    syncControls() {
      this.indicators.forEach((indicator, index) => {
        const isCurrent = index === this.currentIndex;
        indicator.classList.toggle('is-active', isCurrent);
        if (isCurrent) indicator.setAttribute('aria-current', 'true');
        else indicator.removeAttribute('aria-current');
      });

      this.classList.toggle('is-autoplay-paused', !this.autoplayEnabled || this.userPaused);

      this.stage.setAttribute('aria-live', this.autoplayEnabled && !this.userPaused ? 'off' : 'polite');
    }

    syncControlPosition() {
      const media = this.slides[0]?.querySelector('.apella-slideshow__media');
      if (!media) return;
      const mediaHeight = Math.round(media.getBoundingClientRect().height);
      this.style.setProperty('--apella-mobile-arrow-top', `${Math.round(mediaHeight / 2)}px`);
      this.style.setProperty('--apella-mobile-dots-top', `${Math.max(8, mediaHeight - 50)}px`);
      this.style.setProperty('--apella-mobile-bars-top', `${Math.max(0, mediaHeight - 48)}px`);
    }

    scheduleAutoplay(additionalDelay = 0) {
      this.clearAutoplay();
      if (
        !this.autoplayEnabled ||
        this.userPaused ||
        this.focusPaused ||
        document.hidden ||
        this.isAnimating
      ) {
        return;
      }

      const cycleDelay = this.autoplayDelay + Math.max(0, additionalDelay);
      this.style.setProperty('--apella-autoplay-duration', `${cycleDelay}ms`);
      this.restartProgress();
      this.autoplayTimer = window.setTimeout(() => this.showNext('autoplay'), cycleDelay);
    }

    clearAutoplay() {
      this.indicators.forEach((indicator) => {
        indicator.querySelector('[data-autoplay-progress]')?.classList.remove('is-running');
      });
      if (this.autoplayTimer) window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartProgress() {
      const activeProgress = this.indicators[this.currentIndex]?.querySelector('[data-autoplay-progress]');
      if (!activeProgress) return;

      activeProgress.classList.remove('is-running');
      void activeProgress.getBoundingClientRect();
      activeProgress.classList.add('is-running');
    }

    clearContentReveal() {
      this.contentRevealToken += 1;
      if (this.contentDelayTimer) window.clearTimeout(this.contentDelayTimer);
      this.contentDelayTimer = null;
    }

    clearTransition() {
      this.clearContentReveal();
      if (this.transitionTimer) window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }

    onBlockSelect(event) {
      const selectedSlide = event.target.closest?.('[data-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      this.userPaused = true;
      this.syncControls();
      this.goTo(Number.parseInt(selectedSlide.dataset.index, 10), 'next', 'editor');
    }
  }

  customElements.define('apella-slideshow', ApellaSlideshow);
}
